import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import moment from 'moment';
import { useEffect, useRef, useState, type HTMLAttributes } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { editTaskTimeAction, resetEditTaskTimeAction } from 'src/redux/actions/editTaskTimeAction';
import { schedulingTestId } from '../test-ids';
import {
  therapistAssignUndoAction,
  therapistAssignUndoReset,
} from 'src/redux/actions/therapistAssignmentUndoAction';
import {
  therapistAssignTaskAction,
  therapistAssignTaskResetAction,
} from 'src/redux/actions/therapistAssignTaskAction';

type Props = {
  assignGroupData: any;
  setOpenGroupDialog: any;
  providerId: string;
  selectedDate: any;
  therapistIdToDrop: string;
  meta: any;
  setTaskSelectedId: any;
  setCloseGroupDialog: any;
  isV2?: boolean;
  assignTaskPage?: any;
  currentTab: string;
};

function GroupTherapistTask({
  setOpenGroupDialog,
  assignGroupData,
  providerId,
  selectedDate,
  therapistIdToDrop,
  meta,
  isV2,
  setCloseGroupDialog,
  assignTaskPage,
  currentTab,
}: Props) {
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [closeDialog, setCloseDialog] = useState(false);

  const [durationMode, setDurationMode] = useState(false);
  const [numberOfMins, setNumberOfMins] = useState<number>(0);
  const [confirmedConcurrentTaskUuids, setConfirmedConcurrentTaskUuids] = useState<string[]>([]);
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');

  const assignGroupDataFromRedux = useSelector(
    (state: any) => state?.therapistAssignTaskReducer?.response?.data
  );

  const selectedTasksFromRedux = useSelector(
    (state: any) => state?.therapistAssignTaskReducer?.selectedTasks
  );

  const editTaskTimeState = useSelector((state: any) => state?.editTaskTimeReducer);
  const undoAssignState = useSelector((state: any) => state?.therapistAssignUndoReducer);

  // Tracks an in-flight duration-mode operation so we can advance UI only on success.
  // Holds the decision (`type` and `hasMorePatients`) captured at dispatch time.
  const pendingDurationOpRef = useRef<{
    type: 'yes' | 'no';
    concurrentTaskUuids: string[];
    hasMorePatients: boolean;
  } | null>(null);

  useEffect(() => {
    if (assignGroupDataFromRedux?.concurrentAndGroupSession?.length) {
      setDurationMode(true);
      return;
    }
    if (!assignGroupDataFromRedux || !assignGroupDataFromRedux?.label) {
      dispatch(therapistAssignTaskResetAction());
      setAlertSeverity('success');
      setAlertMessage('All task assigned successfully');
      setOpenSnackBar(true);
      setSelectedItems([]);
      setSelectedNames([]);
      setOpenGroupDialog(false);
      setCloseGroupDialog(true);
    } else {
      if (closeDialog) {
        setAlertSeverity('success');
        setAlertMessage('Task assigned successfully, Please select another patient to group with');
        setOpenSnackBar(true);
        setSelectedItems([]);
        setSelectedNames([]);
      }
    }
  }, [assignGroupDataFromRedux]);

  // Watch the editTaskTime API for the durationMode "Yes" path. Only after the
  // backend confirms the duration write do we commit confirmedConcurrentTaskUuids
  // and exit duration mode. On failure we surface an error and let the user retry.
  useEffect(() => {
    const op = pendingDurationOpRef.current;
    if (!op || op.type !== 'yes') return;
    if (editTaskTimeState?.loading) return;

    if (editTaskTimeState?.isTimeEdited) {
      pendingDurationOpRef.current = null;
      setConfirmedConcurrentTaskUuids(op.concurrentTaskUuids);
      if (op.hasMorePatients) {
        setSelectedItems([]);
        setSelectedNames([]);
        setDurationMode(false);
      } else {
        dispatch(therapistAssignTaskResetAction());
        setOpenGroupDialog(false);
        setCloseGroupDialog(true);
      }
      dispatch(resetEditTaskTimeAction());
    } else if (editTaskTimeState?.response !== null) {
      pendingDurationOpRef.current = null;
      setAlertSeverity('error');
      setAlertMessage(
        editTaskTimeState?.response?.message || 'Failed to set session duration. Please try again.'
      );
      setOpenSnackBar(true);
      dispatch(resetEditTaskTimeAction());
    }
  }, [editTaskTimeState]);

  // Watch the assignment-undo API for the durationMode "Cancel" path. Same pattern:
  // only advance UI on success; show an error on failure.
  useEffect(() => {
    const op = pendingDurationOpRef.current;
    if (!op || op.type !== 'no') return;
    if (undoAssignState?.loading) return;

    if (undoAssignState?.isSuccess) {
      pendingDurationOpRef.current = null;
      setConfirmedConcurrentTaskUuids([]);
      if (op.hasMorePatients) {
        setSelectedItems([]);
        setSelectedNames([]);
        setDurationMode(false);
      } else {
        dispatch(therapistAssignTaskResetAction());
        setOpenGroupDialog(false);
        setCloseGroupDialog(true);
      }
      dispatch(therapistAssignUndoReset());
    } else if (undoAssignState?.isError && undoAssignState?.response !== null) {
      pendingDurationOpRef.current = null;
      setAlertSeverity('error');
      setAlertMessage(
        undoAssignState?.response?.message || 'Failed to cancel grouping. Please try again.'
      );
      setOpenSnackBar(true);
      dispatch(therapistAssignUndoReset());
    }
  }, [undoAssignState]);

  const handleYes = () => {
    if (durationMode) {
      const concurrentTaskUuids =
        assignGroupDataFromRedux?.concurrentAndGroupSession?.map((item: any) => item.taskUuid) ||
        [];

      const hasMorePatients =
        assignGroupDataFromRedux.processedData.length ||
        assignGroupDataFromRedux.unprocessedData.length;

      // Capture intent now; advance UI only after the API actually succeeds
      // (handled in the editTaskTimeState effect below).
      pendingDurationOpRef.current = {
        type: 'yes',
        concurrentTaskUuids,
        hasMorePatients: !!hasMorePatients,
      };

      // Reset so the watcher only reacts to this fresh request, not a previous result
      dispatch(resetEditTaskTimeAction());
      dispatch(
        editTaskTimeAction({
          providerId: providerId,
          time: numberOfMins,
          requestBody: {
            taskUuids: concurrentTaskUuids,
            groupUuid: assignGroupDataFromRedux?.concurrentAndGroupSession[0]?.groupUuid,
          },
          isAssigned: true,
        })
      );
      return;
    }

    dispatch(
      therapistAssignTaskAction({
        providerId: providerId,
        therapistId: therapistIdToDrop,
        date: selectedDate,
        currentDate: selectedDate ? moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : '',
        formateDate: moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        refresh: false,
        meta: meta,
        filterByUuid: meta?.filterByUuid,
        destinationAssignPage: assignTaskPage,
        isV2,
        data:
          assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups?.length !== 0
            ? {
                wantToGroup: true,
                unprocessedData: assignGroupData.unprocessedData.map((item: any) => item.uuid),
                processedData: [
                  ...assignGroupData.processedData.map((item: any) => item.uuid),
                  ...confirmedConcurrentTaskUuids,
                ],
                groupUuid: handleDataSelect(selectedItems)[0],
              }
            : {
                wantToGroup: true,
                unprocessedData: [
                  // eslint-disable-next-line no-unsafe-optional-chaining
                  ...assignGroupData?.unprocessedData.map((item: any) => item.uuid),
                  ...handleUnmatchedData(selectedItems),
                ],
                processedData: [
                  ...handleProcessedData(selectedItems),
                  ...confirmedConcurrentTaskUuids,
                ],
                groupUuid: null,
              },
      })
    );
    setCloseDialog(true);
  };

  const handleDataSelect = (items: any) => {
    if (assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups.length !== 0) {
      items = Array.isArray(items) ? items : [items];
    }
    const matchingUUIDs: any = [];

    items?.forEach((name: any) => {
      let match;
      if (assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups.length !== 0) {
        match = assignGroupData.groups.find((item: any) => item.uuid === name);
      } else {
        match = assignGroupData.processedData.find((item: any) => item.uuid === name);
      }
      if (match) {
        matchingUUIDs.push(match.uuid);
      }
    });
    return matchingUUIDs;
  };

  const handleUnmatchedData = (items: any) => {
    const unmatchedUuids = assignGroupData?.processedData
      ?.filter((item: any) => !items.includes(item.uuid))
      .map((item: any) => item.uuid);
    return unmatchedUuids;
  };

  const handleProcessedData = (items: any) => {
    const matchingUUIDs: any = [];
    items.forEach((name: any) => {
      const match = assignGroupData.processedData.find((item: any) => item.uuid === name);
      if (match) {
        matchingUUIDs.push(match.uuid);
      }
    });
    return matchingUUIDs;
  };

  const handleNo = () => {
    if (durationMode) {
      const matchingTaskIds = assignGroupDataFromRedux.concurrentAndGroupSession
        .filter((task: any) => selectedTasksFromRedux.includes(task.taskUuid))
        .map((task: any) => task.taskUuid);

      const hasMorePatients = !!assignGroupDataFromRedux.processedData.length;

      // Capture intent now; advance UI only after the undo API actually succeeds
      // (handled in the undoAssignState effect below).
      pendingDurationOpRef.current = {
        type: 'no',
        concurrentTaskUuids: [],
        hasMorePatients,
      };

      // Reset so the watcher only reacts to this fresh request
      dispatch(therapistAssignUndoReset());
      dispatch(
        therapistAssignUndoAction({
          providerId,
          therapistId: therapistIdToDrop,
          type: '',
          active: true,
          date: selectedDate,
          currentDate: selectedDate ? moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : '',
          formateDate: moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          refresh: false,
          meta: meta,
          filterByUuid: meta?.filterByUuid,
          destinationAssignPage: assignTaskPage,
          discipline: currentTab,
          skill: meta.skill,
          sortBy: meta.skill,
          requestBody: {
            taskUuids: matchingTaskIds,
            groupUuid: null,
          },
          isV2: true,
        })
      );
      return;
    }
    dispatch(
      therapistAssignTaskAction({
        providerId: providerId,
        therapistId: therapistIdToDrop,
        currentDate: selectedDate ? moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : '',
        formateDate: moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        filterByUuid: meta?.filterByUuid,
        date: selectedDate,
        refresh: false,
        meta: meta,
        destinationAssignPage: assignTaskPage,
        isV2,
        data: {
          wantToGroup: false,
          unprocessedData: assignGroupData?.unprocessedData.map((item: any) => item.uuid),
          processedData: [
            ...assignGroupData?.processedData.map((item: any) => item.uuid),
            ...confirmedConcurrentTaskUuids,
          ],
          groupUuid: null,
        },
      })
    );
    setCloseDialog(true);
  };

  const handleChange = (event: any) => {
    const uuids = Array.isArray(event.target.value) ? event.target.value : [event.target.value];
    const originaldata =
      (!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.label) ||
      (!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups?.length === 0)
        ? assignGroupData?.processedData
        : assignGroupData?.groups;

    const result = uuids?.map((targetUUID: string) => {
      const matchingObject = originaldata.find((item: any) => {
        return item.uuid === targetUUID;
      });

      return matchingObject ? matchingObject.patientNames[0] : null;
    });

    const newValue = Array.isArray(event.target.value) ? event.target.value : [event.target.value];

    if (
      (!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.label) ||
      (!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups?.length === 0)
    ) {
      if (assignGroupDataFromRedux?.type === 'GROUP' && newValue.length <= 6) {
        setSelectedItems(newValue);
        setSelectedNames(result);
      }

      if (assignGroupDataFromRedux?.type === 'CONCURRENT' && newValue.length <= 2) {
        setSelectedItems(newValue);
        setSelectedNames(result);
      }
    } else {
      setSelectedItems(newValue);
      setSelectedNames(result);
    }
  };

  const renderProcessedDataOptions = () => {
    return assignGroupData?.processedData?.map((option: any) => (
      <MenuItem
        key={option.uuid}
        value={option.uuid}
      >
        <Checkbox checked={selectedItems?.some((item: any) => item === option.uuid)} />
        <ListItemText primary={option?.patientNames[0]} />
      </MenuItem>
    ));
  };

  const renderGroupDataOptions = () => {
    return assignGroupData?.groups?.map((option: any) => (
      <MenuItem
        key={option.uuid}
        value={option.uuid}
      >
        <Typography>
          {option.patientNames.join(', ').length > 67
            ? option.patientNames.join(', ').substring(0, 67) + '...'
            : option.patientNames.join(', ')}
        </Typography>
      </MenuItem>
    ));
  };

  return (
    <Box width={600}>
      <DialogContent>
        {durationMode ? (
          <>
            <Typography
              mb={3}
              variant="h6"
            >
              Mismatched Durations
            </Typography>

            <Stack
              paddingLeft={'20px'}
              mb={3}
            >
              {assignGroupDataFromRedux?.concurrentAndGroupSession?.map(
                (item: any, index: number) => {
                  return (
                    <Box
                      display={'flex'}
                      columnGap={2}
                      key={index}
                    >
                      <Typography variant="body1">{item.patientName}</Typography>
                      <Typography variant="subtitle1">{item.taskMin} minutes</Typography>
                    </Box>
                  );
                }
              )}
            </Stack>

            <Typography
              variant="body2"
              mb={1}
            >
              Set a session duration for the group. Their plans will update accordingly.
            </Typography>

            <TextField
              fullWidth
              label="Session duration"
              placeholder="Session duration"
              name="numberOfMinutes"
              value={numberOfMins}
              inputProps={{ 'data-testid': schedulingTestId('group-duration') }}
              type="number"
              onChange={(e) => {
                setNumberOfMins(parseInt(e.target.value));
              }}
            />
          </>
        ) : (
          <>
            <Typography
              mb={3}
              variant="h6"
            >
              {assignGroupDataFromRedux?.type === 'GROUP'
                ? assignGroupDataFromRedux?.label?.trim()
                  ? assignGroupDataFromRedux?.label
                  : 'Group Patients for Therapy?'
                : assignGroupDataFromRedux?.type === 'CONCURRENT'
                ? 'Group patients for a concurrent session ?'
                : ''}
            </Typography>
            <FormControl fullWidth>
              <InputLabel
                id="group-session-select-label"
                sx={{
                  background: 'white !important',
                  padding: '0px 5px !important',
                }}
              >
                {assignGroupDataFromRedux?.type === 'GROUP'
                  ? 'Select patients for group session'
                  : assignGroupDataFromRedux?.type === 'CONCURRENT'
                  ? 'Select patients to group with'
                  : ''}
              </InputLabel>
              {(!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.label) ||
              (!assignGroupDataFromRedux?.groups &&
                assignGroupDataFromRedux?.groups?.length === 0) ? (
                <Select
                  labelId="group-session-select-label"
                  multiple
                  value={selectedItems}
                  onChange={handleChange}
                  SelectDisplayProps={
                    {
                      'data-testid': schedulingTestId('group-patients'),
                    } as HTMLAttributes<HTMLDivElement>
                  }
                  renderValue={(selected) => {
                    return selectedNames.join(', ');
                  }}
                >
                  {renderProcessedDataOptions()}
                </Select>
              ) : (
                <Select
                  labelId="group-session-select-label"
                  value={selectedItems}
                  onChange={handleChange}
                  SelectDisplayProps={
                    {
                      'data-testid': schedulingTestId('group-patients'),
                    } as HTMLAttributes<HTMLDivElement>
                  }
                >
                  {renderGroupDataOptions()}
                </Select>
              )}
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          onClick={handleNo}
          data-testid={schedulingTestId('group-cancel')}
          disabled={durationMode && (editTaskTimeState?.loading || undoAssignState?.loading)}
        >
          {durationMode ? 'Cancel' : 'No'}
        </Button>
        <Button
          autoFocus
          variant="contained"
          onClick={handleYes}
          data-testid={schedulingTestId('group-confirm')}
          disabled={
            !durationMode
              ? selectedItems.length === 0
              : !numberOfMins || editTaskTimeState?.loading || undoAssignState?.loading
          }
        >
          {durationMode ? 'Confirm Duration' : 'Yes'}
        </Button>
      </DialogActions>
      <Snackbar
        open={openSnackBar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackBar(false)}
      >
        <Alert
          onClose={() => setOpenSnackBar(false)}
          severity={alertSeverity}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GroupTherapistTask;
